import { Fragment, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { formatBytes, formatDate, getClassNames } from '../../../../../utils/util';
import { VERIFIED, PENDING_VERIFICATION, USER_ACTION_REQUIRED } from '../../../../../utils/constants';

import Button from '../../../../common/button/Button';
import Modal from '../../../../common/modal/Modal';
import TextInput from '../../../../common/inputs/text-input/TextInput';
import FilePicker from '../../../../common/inputs/file-picker/FilerPicker';

import { ReactComponent as VerifiedIcon } from '../../../../../resources/icons/verified.svg';
import { ReactComponent as NotVerifiedIcon } from '../../../../../resources/icons/not-verified.svg';

import './account.scss';

const Account = ({ account, reviewDocument, downloadDocument, uploadDocument }) => {
  const [showModal, setShowModal] = useState(false);
  const [rejectedDocument, setRejectedDocument] = useState(undefined);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});

  const directorIdDocumentFileRef = useRef(null);
  const cikDocumentRef = useRef(null);
  const bankAccountVerificationRef = useRef(null);
  const brokerAuthorisationRef = useRef(null);

  const navigate = useNavigate();

  if (!account) return null;

  const { accountContactInformationVO, companyRepresentatives, verificationDocuments, companyRoles, currentStatus } =
    account;

  const handleRejectClick = (document) => {
    setRejectedDocument(document);
    setShowModal(true);
  };

  const handleAcceptClick = (document) => {
    reviewDocument(document.linkedMediaExternalReference, VERIFIED, undefined, hideRejectionModal);
  };

  const hideRejectionModal = () => {
    setMessage('');
    setErrors((errors) => ({ ...errors, message: false }));
    setRejectedDocument(undefined);
    setShowModal(false);
  };

  const handleSendClick = () => {
    if (!message) {
      setErrors((errors) => ({ ...errors, message: true }));
      return;
    }

    reviewDocument(rejectedDocument.linkedMediaExternalReference, USER_ACTION_REQUIRED, message, hideRejectionModal);
  };

  const renderRepresentatives = () =>
    companyRepresentatives.map((representative, index) => (
      <Fragment key={index}>
        <div className="account__representative">
          <div className="account__field">
            <p className="account__field__label">Title</p>
            <p className="account__field__value">{representative.title ?? '-'}</p>
          </div>
          <div className="account__field">
            <p className="account__field__label">Job Title</p>
            <p className="account__field__value">{representative.jobTitle ?? '-'}</p>
          </div>
          <div className="account__field">
            <p className="account__field__label">First Name</p>
            <p className="account__field__value">{representative.firstName ?? '-'}</p>
          </div>
          <div className="account__field">
            <p className="account__field__label">Last Name</p>
            <p className="account__field__value">{representative.lastName ?? '-'}</p>
          </div>
          <div className="account__field">
            <p className="account__field__label">Email Address</p>
            <p className="account__field__value">{representative.emailAddress ?? '-'}</p>
          </div>
          <div className="account__field">
            <p className="account__field__label">Mobile</p>
            <p className="account__field__value">{representative.phoneNumber ?? '-'}</p>
          </div>
        </div>
        {index !== companyRepresentatives.length - 1 && <div className="account__seperator" />}
      </Fragment>
    ));

  const renderDocuments = (subject) => {
    const filteredDocuments = verificationDocuments.filter((document) => document.subject === subject);

    if (filteredDocuments.length === 0) {
      return <p className="account__no-documents">No documents found</p>;
    }

    const sortedDocuments = filteredDocuments.sort((documentOne, documentTwo) => {
      const documentOneName = documentOne.name.toLowerCase();
      const documentTwoName = documentTwo.name.toLowerCase();

      if (documentOneName < documentTwoName) return 1;
      if (documentOneName > documentTwoName) return -1;
      return 0;
    });

    return sortedDocuments.map((document, index) => {
      const status = document.status.toLowerCase();

      return (
        <div className="account__document" key={index}>
          <div className="account__document__details">
            <p className="account__document__name" onClick={() => downloadDocument(document)}>
              {document.name}
            </p>
            <p className="account__document__size">{formatBytes(document.byteSize)}</p>
          </div>
          <div className="account__document__buttons">
            <Button
              className={getClassNames('account__document__button', {
                grey: status === USER_ACTION_REQUIRED.toLowerCase(),
              })}
              onClick={() => handleRejectClick(document)}
              disabled={status !== PENDING_VERIFICATION.toLowerCase()}
              secondary
            >
              {status === USER_ACTION_REQUIRED.toLowerCase() ? 'Rejected' : 'Reject'}
            </Button>
            <Button
              className={getClassNames('account__document__button', { grey: status === VERIFIED.toLowerCase() })}
              disabled={status !== PENDING_VERIFICATION.toLowerCase()}
              onClick={() => handleAcceptClick(document)}
            >
              {status === VERIFIED.toLowerCase() ? 'Accepted' : 'Accept'}
            </Button>
          </div>
        </div>
      );
    });
  };

  return (
    <>
      <section className="account">
        <section className="account__header">
          <h1 className="account__header__title">Account Details</h1>
          <button className="account__header__button" onClick={() => navigate(-1)}>
            {'<'} Back
          </button>
        </section>
        <section className="account__details">
          <section className="account__details__left">
            <section className="account__company-information">
              <h2>Company Information</h2>

              <div className="account__company-information__details">
                <div className="account__field">
                  <p className="account__field__label">Company Name</p>
                  <p className="account__field__value">{account.companyName ?? '-'}</p>
                </div>
                <div className="account__field">
                  <p className="account__field__label">Company Registration No.</p>
                  <p className="account__field__value">{account.companyRegistrationNo ?? '-'}</p>
                </div>
                <div className="account__field">
                  <p className="account__field__label">Trading Name</p>
                  <p className="account__field__value">{account.tradingName ?? '-'}</p>
                </div>
                <div className="account__field">
                  <p className="account__field__label">VAT No.</p>
                  <p className="account__field__value">{account.vatNo ?? '-'}</p>
                </div>
                <div className="account__field">
                  <p className="account__field__label">Incorporation Date</p>
                  <p className="account__field__value">{formatDate(account.incorporationDate) ?? '-'}</p>
                </div>
                <div className="account__field">
                  <p className="account__field__label">Importer Code</p>
                  <p className="account__field__value">{account.importerCode ?? '-'}</p>
                </div>
                <div className="account__field">
                  <p className="account__field__label">Web URL</p>
                  <p className="account__field__value">{account.webURL ?? '-'}</p>
                </div>
                <div className="account__field">
                  <p className="account__field__label">Sage Account Number</p>
                  <p className="account__field__value">{account.sageAccountNumber ?? '-'}</p>
                </div>
              </div>
            </section>

            <section className="account__company-contact-information">
              <h2>Company Contact Information</h2>

              <div className="account__company-contact-information__details">
                <div className="account__field">
                  <p className="account__field__label">Address Line 1</p>
                  <p className="account__field__value">{accountContactInformationVO?.addressLine1 ?? '-'}</p>
                </div>
                <div className="account__field">
                  <p className="account__field__label">Address Line 2</p>
                  <p className="account__field__value">{accountContactInformationVO?.addressLine2 ?? '-'}</p>
                </div>
                <div className="account__field">
                  <p className="account__field__label">City</p>
                  <p className="account__field__value">{accountContactInformationVO?.city ?? '-'}</p>
                </div>
                <div className="account__field">
                  <p className="account__field__label">Province/Region/State</p>
                  <p className="account__field__value">{accountContactInformationVO?.provinceRegionState ?? '-'}</p>
                </div>
                <div className="account__field">
                  <p className="account__field__label">Country</p>
                  <p className="account__field__value">{accountContactInformationVO?.country ?? '-'}</p>
                </div>
                <div className="account__field">
                  <p className="account__field__label">Postal Code</p>
                  <p className="account__field__value">{accountContactInformationVO?.postalCode ?? '-'}</p>
                </div>
              </div>
            </section>

            <section className="account__verification-details">
              <h2>Verification Details</h2>
              <p className="account__verification-details__roles">{companyRoles.join(' / ')}</p>

              <h3>Current Status</h3>
              <div className="account__verification-details__status">
                <p>{currentStatus}</p>
                {currentStatus === 'Verified' ? <VerifiedIcon /> : <NotVerifiedIcon />}
              </div>

              <h3>Verification Documents</h3>
              <div className="account__verification-details__document-type-container">
                <p className="account__verification-details__document-type">ID Document of Director</p>
                <FilePicker
                  fileRef={directorIdDocumentFileRef}
                  onChange={({ target }) => {
                    uploadDocument('ID Document of Director', 'general verification', target.files[0]);
                  }}
                  triggerElement={<Button onClick={() => directorIdDocumentFileRef.current.click()}>Upload</Button>}
                  enableFileLimit
                />
              </div>
              {renderDocuments('ID Document of Director')}

              <div className="account__verification-details__document-type-container">
                <p className="account__verification-details__document-type">Central Index Key (CIK) Document</p>
                <FilePicker
                  fileRef={cikDocumentRef}
                  onChange={({ target }) => {
                    uploadDocument('Central Index Key (CIK) Document', 'general verification', target.files[0]);
                  }}
                  triggerElement={<Button onClick={() => cikDocumentRef.current.click()}>Upload</Button>}
                  enableFileLimit
                />
              </div>
              {renderDocuments('Central Index Key (CIK) Document')}

              <div className="account__verification-details__document-type-container">
                <p className="account__verification-details__document-type">Bank Account Verification Document</p>
                <FilePicker
                  fileRef={bankAccountVerificationRef}
                  onChange={({ target }) => {
                    uploadDocument('Bank Account Verification Document', 'general verification', target.files[0]);
                  }}
                  triggerElement={<Button onClick={() => bankAccountVerificationRef.current.click()}>Upload</Button>}
                  enableFileLimit
                />
              </div>
              {renderDocuments('Bank Account Verification Document')}

              {companyRoles?.includes('Broker') && (
                <>
                  <div className="account__verification-details__document-type-container">
                    <p className="account__verification-details__document-type">Broker Authorisation Letters</p>
                    <FilePicker
                      fileRef={brokerAuthorisationRef}
                      onChange={({ target }) => {
                        uploadDocument('Broker Authorisation Letters', 'broker verification', target.files[0]);
                      }}
                      triggerElement={<Button onClick={() => brokerAuthorisationRef.current.click()}>Upload</Button>}
                      enableFileLimit
                    />
                  </div>
                  {renderDocuments('Broker Authorisation Letters')}
                </>
              )}
            </section>
          </section>

          <section className="account__details__right">
            <section className="account__company-representatives">
              <h2>Company Representatives</h2>
              {renderRepresentatives()}
            </section>
          </section>
        </section>
      </section>

      <Modal show={showModal} setShow={setShowModal}>
        <section className="account-management__rejection-modal">
          <h2>Reject Document</h2>
          <p>The member will receive a notification along with any comments added below:</p>
          <TextInput
            name="rejection-message"
            placeholder="Comments..."
            value={message}
            onChange={({ target }) => setMessage(target.value)}
            error={errors.message}
            multiline
          />

          <section className="account-management__rejection-modal__actions">
            <Button className="account-management__rejection-modal__button" onClick={hideRejectionModal} secondary>
              Cancel
            </Button>

            <Button className="account-management__rejection-modal__button" onClick={handleSendClick}>
              Send
            </Button>
          </section>
        </section>
      </Modal>
    </>
  );
};

Account.propTypes = {
  account: PropTypes.shape({}),
  reviewDocument: PropTypes.func.isRequired,
  downloadDocument: PropTypes.func.isRequired,
};

Account.defaultProps = {
  account: undefined,
};

export default Account;
